import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/client/lib/utils';

// Button system, glassmorphism brand edition.
// "primary" is a gradient pill with inner light glow.
// "ghost" is a glass pill that floats above ambient backgrounds.
// "secondary" is a refined white-glass slab with subtle border.
// "link" is unchanged.
const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-2 font-sans font-medium',
    'transition-all duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-55',
    'select-none whitespace-nowrap',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'text-white rounded-pill',
          'bg-gradient-to-br from-primary to-primary-dark',
          'shadow-[0_4px_16px_rgba(117,59,189,0.28),inset_0_1px_0_rgba(255,255,255,0.18)]',
          'hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(117,59,189,0.38),inset_0_1px_0_rgba(255,255,255,0.2)]',
          'active:translate-y-0 active:scale-[0.98]',
          'before:absolute before:inset-0 before:rounded-pill',
          'before:bg-gradient-to-br before:from-white/20 before:to-transparent',
          'before:pointer-events-none before:opacity-100',
        ].join(' '),
        secondary: [
          'text-text-primary rounded-pill',
          'bg-white/90 backdrop-blur-md border border-glass-border-strong',
          'shadow-glass-soft',
          'hover:bg-white hover:border-primary/30 hover:-translate-y-px hover:shadow-glass',
          'active:translate-y-0',
        ].join(' '),
        ghost: [
          'text-primary rounded-pill',
          'bg-glass-bg backdrop-blur-md border border-glass-border-strong',
          'hover:bg-white/95 hover:border-primary/30 hover:-translate-y-px',
        ].join(' '),
        outline: [
          'text-primary rounded-pill border-2 border-primary',
          'bg-transparent',
          'hover:bg-primary hover:text-white hover:-translate-y-px hover:shadow-glass',
        ].join(' '),
        danger: [
          'text-white rounded-pill',
          'bg-gradient-to-br from-error to-[#8a1f1a]',
          'shadow-[0_4px_16px_rgba(196,50,45,0.28)]',
          'hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(196,50,45,0.38)]',
        ].join(' '),
        link: 'bg-transparent text-primary underline-offset-4 hover:underline px-0 rounded-none',
      },
      size: {
        xs: 'h-8 px-3 text-body-small',
        sm: 'h-9 px-4 text-body-small',
        md: 'h-11 px-5 text-body',
        lg: 'h-[3.25rem] px-7 text-body-large',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, children, ...props },
  ref,
) {
  return (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
      <span className="relative z-[1] inline-flex items-center gap-2">{children}</span>
    </button>
  );
});
